import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../utils/api';

// Async Thunks
export const fetchDashboardData = createAsyncThunk(
  'account/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch('/api/dashboard');
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to fetch dashboard data');
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchProfileData = createAsyncThunk(
  'account/fetchProfileData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch('/api/profile');
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to fetch profile data');
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateProfileData = createAsyncThunk(
  'account/updateProfileData',
  async ({ formData }, { rejectWithValue }) => {
    try {
      // formData is a FormData object (multipart/form-data)
      const res = await apiFetch('/api/profile', {
        method: 'PUT',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to update profile');
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'account/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await apiFetch('/api/profile/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to change password');
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchUserBills = createAsyncThunk(
  'account/fetchUserBills',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch('/api/bills');
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to fetch bills');
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addAddress = createAsyncThunk(
  'account/addAddress',
  async ({ addressData }, { rejectWithValue }) => {
    try {
      const res = await apiFetch('/api/profile/address', {
        method: 'POST',
        body: JSON.stringify(addressData)
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to add address');
      return data; // returns updated list of addresses
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateAddress = createAsyncThunk(
  'account/updateAddress',
  async ({ addressId, addressData }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/api/profile/address/${addressId}`, {
        method: 'PUT',
        body: JSON.stringify(addressData)
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to update address');
      return data; // returns updated list of addresses
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'account/deleteAddress',
  async ({ addressId }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/api/profile/address/${addressId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to delete address');
      return data; // returns updated list of addresses
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  'account/setDefaultAddress',
  async ({ addressId }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/api/profile/address/${addressId}/default`, {
        method: 'PATCH'
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Failed to set default address');
      return data; // returns updated list of addresses
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState: {
    dashboard: {
      profile: {},
      stats: {
        totalOrders: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        addressCount: 0,
        lastOrderDate: null
      },
      recentOrders: [],
      recentBills: []
    },
    profile: {
      name: '',
      email: '',
      phone: '',
      shopName: '',
      avatar: '',
      isProfileCompleted: false,
      addresses: []
    },
    bills: [],
    addresses: [],
    loading: false,
    error: null
  },
  reducers: {
    clearAccountErrors: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
        // Keep synced
        if (action.payload.profile) {
          state.profile = { ...state.profile, ...action.payload.profile };
        }
        if (action.payload.profile?.addresses) {
          state.addresses = action.payload.profile.addresses;
        }
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Profile
      .addCase(fetchProfileData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.addresses = action.payload.addresses || [];
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Profile
      .addCase(updateProfileData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfileData.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.addresses = action.payload.addresses || [];
        state.dashboard.profile = {
          name: action.payload.name,
          email: action.payload.email,
          phone: action.payload.phone,
          shopName: action.payload.shopName,
          avatar: action.payload.avatar
        };
      })
      .addCase(updateProfileData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Bills
      .addCase(fetchUserBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserBills.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = action.payload;
      })
      .addCase(fetchUserBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })

      // Address Operations (shared fulfilled states as they all return updated address list)
      .addMatcher(
        (action) =>
          [
            addAddress.fulfilled.type,
            updateAddress.fulfilled.type,
            deleteAddress.fulfilled.type,
            setDefaultAddress.fulfilled.type
          ].includes(action.type),
        (state, action) => {
          state.loading = false;
          state.addresses = action.payload;
          state.profile.addresses = action.payload;
          state.dashboard.stats.addressCount = action.payload.length;
        }
      )
      .addMatcher(
        (action) =>
          [
            addAddress.pending.type,
            updateAddress.pending.type,
            deleteAddress.pending.type,
            setDefaultAddress.pending.type,
            changePassword.pending.type
          ].includes(action.type),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          [
            addAddress.rejected.type,
            updateAddress.rejected.type,
            deleteAddress.rejected.type,
            setDefaultAddress.rejected.type,
            changePassword.rejected.type
          ].includes(action.type),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  }
});

export const { clearAccountErrors } = accountSlice.actions;
export default accountSlice.reducer;
